import {
  getOverdueTasksFromTree,
  onDeadlineDeadlineReorder,
} from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useDeadlineDeadlineTree } from '../../hooks/use-deadline-deadline-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './deadline-deadline-grouping-tasks-list.style';
import {
  DeadlineDeadlineRenderData,
  DeadlineDeadlineRenderDataContext,
} from './deadline-deadline-render-context';
import { DeadlineDeadlineRenderItem } from './deadline-deadline-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

export const DeadlineDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDeadlineDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDeadlineDeadlineReorder({ updatedItems, tasksMap });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );

  const renderData = useMemo<DeadlineDeadlineRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      overdueTasks,
      today,
      denseFormat,
    }),
    [groupSettings, tasksMap, tasks, overdueTasks, today, denseFormat],
  );

  const [tree] = useTree();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <DeadlineDeadlineRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={DeadlineDeadlineRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </DeadlineDeadlineRenderDataContext.Provider>
    </Styled.Container>
  );
};
